/** @jsxImportSource react */
import { useEffect, useState } from 'react'
import { Autocomplete, CircularProgress, Link, TextField, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { recordsAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { entityLabel } from '../../utils/helpers'

/** Title of a record by id (cached); used where only the id is known. */
export const useRecordTitle = (id, knownTitle) => {
  const tenantId = useTenantKey()
  const query = useQuery({
    queryKey: [tenantId, 'records', 'one', id],
    queryFn: () => recordsAPI.get(id),
    enabled: !!id && !knownTitle,
    retry: false,
    staleTime: 60_000,
  })
  if (!id) return null
  if (knownTitle) return knownTitle
  if (query.isError) return '(not found)'
  return query.data?.title ?? '…'
}

export const LookupTitle = ({ id, title }) => <>{useRecordTitle(id, title)}</>

/** Clickable name of a linked record; struck through when the target was deleted. */
export const LookupLink = ({ refValue }) => {
  if (!refValue) return '—'
  // person (USER) values: a member, not a record; no page to link to
  if (refValue.entityType === 'USER') {
    return (
      <Typography component="span" variant="body2" color={refValue.deleted ? 'text.secondary' : 'inherit'}
        sx={refValue.deleted ? { textDecoration: 'line-through' } : undefined} title={refValue.deleted ? 'No longer a member' : undefined}>
        {refValue.title}
      </Typography>
    )
  }
  if (refValue.deleted) {
    return <Typography component="span" variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>{refValue.title}</Typography>
  }
  return (
    <Link component={RouterLink} to={`/records/${refValue.entityType}/${refValue.id}`} underline="hover" onClick={(e) => e.stopPropagation()}>
      {refValue.title}
    </Link>
  )
}

/**
 * Searchable picker for a LOOKUP field. Value is the linked record's id; `initialRef` (from the
 * record's resolved lookups) provides its title without another request.
 */
const LookupInput = ({ field, value, onChange, initialRef, label, required, error, helperText, disabled, size }) => {
  const tenantId = useTenantKey()
  const target = field.lookupEntityType
  const [input, setInput] = useState('')
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  // no link yet: both are undefined, so check initialRef itself before comparing ids
  const selectedTitle = useRecordTitle(value, initialRef && initialRef.id === value ? initialRef.title : null)

  useEffect(() => {
    const handle = setTimeout(() => setTerm(input), 250)
    return () => clearTimeout(handle)
  }, [input])

  const { data, isFetching } = useQuery({
    queryKey: [tenantId, 'records', target, 'lookup', term],
    queryFn: () => recordsAPI.list({ entityType: target, q: term, size: 10, sort: 'updatedOn,desc' }),
    enabled: open && !!target,
    placeholderData: keepPreviousData,
  })
  const options = (data?.items || []).map((r) => ({ id: r.id, title: r.title }))
  const selected = value ? { id: value, title: selectedTitle } : null

  return (
    <Autocomplete
      size={size}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disabled={disabled}
      value={selected}
      options={selected && !options.some((o) => o.id === selected.id) ? [selected, ...options] : options}
      filterOptions={(x) => x}
      getOptionLabel={(o) => o.title || ''}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, next) => onChange(next ? next.id : '')}
      onInputChange={(_, next, reason) => { if (reason === 'input') setInput(next) }}
      loading={isFetching}
      noOptionsText={term ? 'No matches' : `No ${entityLabel(target || 'record').toLowerCase()} records yet`}
      renderOption={(props, option) => <li {...props} key={option.id}>{option.title}</li>}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? field.label}
          required={required}
          error={!!error}
          helperText={error || helperText}
          placeholder={`Search ${entityLabel(target || 'record').toLowerCase()}…`}
          InputProps={{ ...params.InputProps, endAdornment: <>{isFetching && open ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }}
        />
      )}
    />
  )
}

export default LookupInput
